import { DocLink } from "../components/Legal";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { del } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { HandleSheet } from "../components/HandleSheet";
import { Shell } from "../components/Shell";
import { Sheet, useToast } from "../components/ui";
import { fmtDate } from "../lib/format";
import { DOCS } from "../lib/legal";
import { connectMessage, openYoutubeConnect, useConnectResult, useYoutube, useYoutubeReturn } from "../lib/youtube";
import { planName } from "./Videos";

export default function Account() {
  const { me, plan, signOut } = useAuth();
  const nav = useNavigate();
  const yt = useYoutube(!!me?.features?.youtube);
  const [toast, say] = useToast();
  const [confirmOff, setConfirmOff] = useState(false);
  const [editHandle, setEditHandle] = useState(false);
  const conn = yt.data?.connection ?? null;
  // Connect opens Google's page in a new window (lib/youtube.ts); a channel verified on YouTube since is read again on return.
  useConnectResult((result) => {
    say(connectMessage(result));
    void yt.reload();
  }, () => void (conn && conn.state === "active" && !conn.long_uploads ? yt.refresh() : undefined));
  useYoutubeReturn(say, () => void yt.reload());
  if (!me) return null;
  const u = me.usage;
  const disconnect = async () => {
    setConfirmOff(false);
    try {
      await del("/v1/youtube");
      await yt.reload();
      say("YouTube is disconnected.");
    } catch (e) {
      say((e as Error).message);
    }
  };
  return (
    <Shell title="Account" back="/" active="Account" right={<span />}>
      <div className="p" style={{ paddingTop: 24, paddingBottom: 40, maxWidth: 560 }} data-testid="account">
        <h1>{me.user.display_name}</h1>
        <p className="sub mt6">{me.user.email}</p>
        <div className="list mt24">
          <div data-testid="account-handle-row">
            <span>Handle</span>
            <span style={{ marginLeft: "auto" }}>
              {me.user.handle ? <span className="med" data-testid="account-handle">@{me.user.handle}</span> : <span className="tiny">Not set</span>} ·{" "}
              <button type="button" className="u" onClick={() => setEditHandle(true)} data-testid="account-change-handle">Change</button>
            </span>
          </div>
          {me.user.handle && (
            <div data-testid="account-user-page-row">
              <span>User page</span>
              <span style={{ marginLeft: "auto" }}>
                <Link className="u" to={`/u/${me.user.handle}`}>/u/{me.user.handle}</Link>
              </span>
            </div>
          )}
          <div>
            <span>Teams</span>
            <span style={{ marginLeft: "auto" }}>
              {me.teams && me.teams.length > 0 ? `${me.teams.length} ${me.teams.length === 1 ? "team" : "teams"} · ` : ""}
              <Link className="u" to="/teams">Open</Link>
            </span>
          </div>
          <div><span>Plan</span><span style={{ marginLeft: "auto" }} className="med">{planName(plan)}</span></div>
          {u && <div><span>This month</span><span style={{ marginLeft: "auto" }}>{u.videos_used} of {u.videos_per_month} videos · resets {fmtDate(u.period_end)}</span></div>}
          {u && <div><span>Clips kept</span><span style={{ marginLeft: "auto" }}>{u.retention_days} days</span></div>}
          <div data-testid="acct-terms">
            <span>Terms of use</span>
            <span style={{ marginLeft: "auto", textAlign: "right" }}>{me.beta?.accepted ? `${me.beta.accepted.version} · agreed ${fmtDate(me.beta.accepted.at)}` : DOCS.beta_terms.version} · <DocLink kind="beta_terms">Read</DocLink></span>
          </div>
          <div data-testid="acct-privacy"><span>Privacy policy</span><span style={{ marginLeft: "auto", textAlign: "right" }}>{DOCS.privacy.version} · <DocLink kind="privacy">Read</DocLink></span></div>
          {me.features?.youtube && (
            <div data-testid="yt-row">
              <span>YouTube</span>
              <span style={{ marginLeft: "auto", textAlign: "right" }}>
                {!yt.data ? (
                  <span className="tiny">Checking…</span>
                ) : !conn ? (
                  <button className="u" onClick={openYoutubeConnect} data-testid="yt-connect">Connect</button>
                ) : conn.state === "active" ? (
                  <>
                    <span className="med" data-testid="yt-channel">{conn.channel_title}</span> · <button className="u" onClick={() => setConfirmOff(true)} data-testid="yt-disconnect">Disconnect</button>
                    {!conn.long_uploads && <span className="tiny" style={{ display: "block", marginTop: 2 }} data-testid="yt-verify-line">Takes videos up to 15 minutes · <a className="u" href={conn.verify_url} target="_blank" rel="noreferrer" data-testid="yt-verify">Verify on YouTube</a></span>}
                  </>
                ) : (
                  <><span className="bad">Needs connecting again</span> · <button className="u" onClick={openYoutubeConnect} data-testid="yt-connect">Reconnect</button></>
                )}
              </span>
            </div>
          )}
          <div><span>Public display name, privacy</span><span className="tiny" style={{ marginLeft: "auto" }}>coming with moments</span></div>
          {me.user.is_operator && <div><span>Operators</span><span style={{ marginLeft: "auto" }}><Link className="u" to="/admin/live">Live</Link> · <Link className="u" to="/admin/pipeline">Pipeline</Link> · <Link className="u" to="/held">Held for review</Link> · <Link className="u" to="/admin/bugs">Bug reports</Link> · <Link className="u" to="/admin/invites">Invites</Link></span></div>}
        </div>
        <div className="mt24 stack" style={{ gap: 10 }}>
          <button className="btn ghost full" onClick={() => nav("/plans")}>Change plan</button>
          <button className="btn ghost full" onClick={() => signOut().then(() => nav("/signin", { replace: true }))} data-testid="sign-out">Sign out</button>
          <button className="btn ghost full" disabled title="Account deletion is not built in the API yet">Delete account</button>
        </div>
        <p className="tiny mt16">Account deletion and label-export consent arrive with the account settings API.</p>
        {me.features?.youtube && (
          <p className="tiny mt10">Deep Third posts to YouTube only the videos you switch on when you upload them. You can also remove its access in your <a className="u" href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer">Google account</a>.</p>
        )}
      </div>
      {confirmOff && conn && (
        <Sheet onClose={() => setConfirmOff(false)}>
          <div data-testid="yt-off-sheet">
            <h2>Disconnect YouTube?</h2>
            <p className="sub mt10">Deep Third stops posting to {conn.channel_title} and deletes what it kept from YouTube. A post that has not finished is cancelled. Videos already posted stay on YouTube.</p>
            <div className="mt24"><button className="btn dark full" onClick={disconnect} data-testid="yt-off-confirm">Disconnect</button></div>
            <div className="mt10"><button className="btn ghost full" onClick={() => setConfirmOff(false)}>Keep it</button></div>
          </div>
        </Sheet>
      )}
      {editHandle && (
        <HandleSheet
          initialHandle={me.user.handle}
          onClose={() => setEditHandle(false)}
          onSaved={(h) => say(`Handle saved: @${h}`)}
        />
      )}
      {toast}
    </Shell>
  );
}
